import { DATE_FORMAT } from "@/utils/date-time-utility";
import * as Yup from "yup";
import { AsyncSelectOption } from "@/components/AsyncSelect";
import type {
  CustomFrequencyUnit,
  EndType,
  RecurringFrequency,
} from "@/components/app/provider/scheduling/RecurringConfig";
import { ChipSelectorItem } from "@/components/ChipSelector";
import { CHART_COLORS } from "@/components/app/provider/patient/chart";
import type { Option } from "@/constants/common";
// Leaf import: the group-appointment barrel loads group-visit back and forms a cycle.
import { GROUP_APPOINTMENT_STATUS } from "@/pages/app/provider/scheduling/group-appointment/group-appointment-constant";
import {
  EncounterSumaryFormTypeEnum,
  type GroupVisitSessionModeEnum,
} from "@/services";
import {
  GroupFieldsValues,
  GroupFormValues,
  SessionFrequency,
  GroupActivityOption,
  SessionCheckboxSection,
  GroupNoteTemplate,
  GroupType,
  SessionNoteState,
  SessionNoteValues,
  TherapyCategory,
} from "./group-visit-types";

export const GROUP_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ARCHIVED: "ARCHIVED",
} as const;

/**
 * Archiving is offered only once a group is inactive.
 *
 * @author Rajkumar Rathod
 */
export const ARCHIVABLE_STATUSES: string[] = [GROUP_STATUS.INACTIVE];

export const GROUP_STATUS_VALUES = Object.values(GROUP_STATUS);

export const GROUP_STATUS_LABEL: Record<string, string> = {
  [GROUP_STATUS.ACTIVE]: "Active",
  [GROUP_STATUS.INACTIVE]: "Inactive",
  [GROUP_STATUS.ARCHIVED]: "Archived",
};

export const GROUP_TYPE = {
  CBT_GROUP: "CBT Group",
  DBT_GROUP: "DBT Group",
  ACT_GROUP: "ACT Group",
  TRAUMA_FOCUSED_GROUP: "Trauma-Focused Group",
  GROUP_PSYCHOTHERAPY: "Group Psychotherapy",
  SUPPORTIVE_THERAPY_GROUP: "Supportive Therapy Group",
  BEHAVIORAL_THERAPY_GROUP: "Behavioral Therapy Group",
  DEPRESSION_SUPPORT_GROUP: "Depression Support Group",
  ANXIETY_SUPPORT_GROUP: "Anxiety Support Group",
  GRIEF_AND_LOSS_GROUP: "Grief & Loss Group",
  RECOVERY_SUPPORT_GROUP: "Recovery Support Group",
  MINDFULNESS_GROUP: "Mindfulness Group",
  EMOTIONAL_REGULATION_GROUP: "Emotional Regulation Group",
  DISTRESS_TOLERANCE_GROUP: "Distress Tolerance Group",
  INTERPERSONAL_EFFECTIVENESS_GROUP: "Interpersonal Effectiveness Group",
  COPING_SKILLS_GROUP: "Coping Skills Group",
  STRESS_MANAGEMENT_GROUP: "Stress Management Group",
  ANGER_MANAGEMENT_GROUP: "Anger Management Group",
  SLEEP_MANAGEMENT_GROUP: "Sleep Management Group",
  MEDICATION_EDUCATION_GROUP: "Medication Education Group",
  MENTAL_HEALTH_EDUCATION_GROUP: "Mental Health Education Group",
  RELAPSE_PREVENTION_GROUP: "Relapse Prevention Group",
  FAMILY_PSYCHOEDUCATION_GROUP: "Family Psychoeducation Group",
  CAREGIVER_SUPPORT_GROUP: "Caregiver Support Group",
  FAMILY_THERAPY_GROUP: "Family Therapy Group",
  COCM_GROUP: "CoCM Group",
  BHI_GROUP: "BHI Group",
  PSYCHIATRIC_SKILLS_GROUP: "Psychiatric Skills Group",
  SYMPTOM_MANAGEMENT_GROUP: "Symptom Management Group",
  MEDICATION_ADHERENCE_GROUP: "Medication Adherence Group",
} as const;

export const THERAPY_CATEGORY = {
  COGNITIVE_BEHAVIORAL_THERAPY_PSYCHOTHERAPY:
    "Cognitive Behavioral Therapy / Psychotherapy",
  DIALECTICAL_BEHAVIOR_THERAPY_PSYCHOTHERAPY:
    "Dialectical Behavior Therapy / Psychotherapy",
  ACCEPTANCE_AND_COMMITMENT_THERAPY_PSYCHOTHERAPY:
    "Acceptance & Commitment Therapy / Psychotherapy",
  TRAUMA_FOCUSED_PSYCHOTHERAPY: "Trauma-Focused Psychotherapy",
  GROUP_PSYCHOTHERAPY: "Group Psychotherapy",
  BEHAVIORAL_PSYCHOTHERAPY: "Behavioral Psychotherapy",
  SUPPORTIVE_PSYCHOTHERAPY: "Supportive Psychotherapy",
  SUPPORTIVE_SLASH_PSYCHOTHERAPY: "Supportive / Psychotherapy",
  BEHAVIORAL_HEALTH_PSYCHOTHERAPY: "Behavioral Health / Psychotherapy",
  SKILLS_BASED_THERAPY: "Skills-Based Therapy",
  SKILLS_BASED_THERAPY_DBT: "Skills-Based Therapy / DBT",
  DBT_SKILLS_BASED_THERAPY: "DBT / Skills-Based Therapy",
  PSYCHOEDUCATION_SKILLS_BASED_THERAPY:
    "Psychoeducation / Skills-Based Therapy",
  PSYCHOEDUCATION_BEHAVIORAL_THERAPY: "Psychoeducation / Behavioral Therapy",
  PSYCHOEDUCATION: "Psychoeducation",
  PSYCHOEDUCATION_PSYCHIATRY: "Psychoeducation / Psychiatry",
  FAMILY_THERAPY_PSYCHOEDUCATION: "Family Therapy / Psychoeducation",
  FAMILY_CAREGIVER_SUPPORT: "Family/Caregiver Support",
  FAMILY_THERAPY_PSYCHOTHERAPY: "Family Therapy / Psychotherapy",
  COLLABORATIVE_CARE_MODEL: "Collaborative Care Model",
  BEHAVIORAL_HEALTH_INTEGRATION: "Behavioral Health Integration",
  PSYCHIATRY_BEHAVIORAL_THERAPY: "Psychiatry / Behavioral Therapy",
  PSYCHIATRY_PSYCHOEDUCATION: "Psychiatry / Psychoeducation",
} as const;
/** Both are stored as their own label, so the value round-trips as sent. */
export const GROUP_TYPE_OPTIONS: Option[] = Object.values(GROUP_TYPE).map(
  (value) => ({ value, label: value })
);

export const THERAPY_CATEGORY_OPTIONS: Option[] = Object.values(
  THERAPY_CATEGORY
).map((value) => ({ value, label: value }));

/** Visit note templates a group session can be documented with. */
export const GROUP_NOTE_TEMPLATE = {
  PSYCHOTHERAPY_NOTE: EncounterSumaryFormTypeEnum.PsychotherapyNote,
  DAP_NOTE: EncounterSumaryFormTypeEnum.DapNote,
  BIRP_NOTE: EncounterSumaryFormTypeEnum.BirpNote,
  PROGRESS_NOTE: EncounterSumaryFormTypeEnum.ProgressNote,
  PSYCHOEDUCATION_THERAPY: EncounterSumaryFormTypeEnum.PsychoeducationTherapy,
  PSYCHIATRIC_PROGRESS_EXAM: EncounterSumaryFormTypeEnum.PsychiatricProgress,
  COLLABORATIVE_CARE_MODEL: EncounterSumaryFormTypeEnum.CollaborativeCareModel,
} as const;

export const GROUP_NOTE_TEMPLATE_LABEL: Record<GroupNoteTemplate, string> = {
  [GROUP_NOTE_TEMPLATE.PSYCHOTHERAPY_NOTE]: "Psychotherapy Note",
  [GROUP_NOTE_TEMPLATE.DAP_NOTE]: "DAP Note",
  [GROUP_NOTE_TEMPLATE.BIRP_NOTE]: "BIRP Note",
  [GROUP_NOTE_TEMPLATE.PROGRESS_NOTE]: "Progress Note",
  [GROUP_NOTE_TEMPLATE.PSYCHOEDUCATION_THERAPY]: "Psychoeducation Therapy",
  [GROUP_NOTE_TEMPLATE.PSYCHIATRIC_PROGRESS_EXAM]: "Psychiatric Progress Exam",
  [GROUP_NOTE_TEMPLATE.COLLABORATIVE_CARE_MODEL]: "Collaborative Care Model",
};

/** Note template sets the table pairs with a group type. */
const GROUP_NOTE_TEMPLATE_SET = {
  FULL: [
    GROUP_NOTE_TEMPLATE.PSYCHOTHERAPY_NOTE,
    GROUP_NOTE_TEMPLATE.DAP_NOTE,
    GROUP_NOTE_TEMPLATE.BIRP_NOTE,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  PSYCHOEDUCATION_PSYCHOTHERAPY: [
    GROUP_NOTE_TEMPLATE.PSYCHOEDUCATION_THERAPY,
    GROUP_NOTE_TEMPLATE.PSYCHOTHERAPY_NOTE,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  PSYCHOEDUCATION: [
    GROUP_NOTE_TEMPLATE.PSYCHOEDUCATION_THERAPY,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  PSYCHOEDUCATION_PSYCHIATRIC: [
    GROUP_NOTE_TEMPLATE.PSYCHOEDUCATION_THERAPY,
    GROUP_NOTE_TEMPLATE.PSYCHIATRIC_PROGRESS_EXAM,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  COLLABORATIVE_CARE: [
    GROUP_NOTE_TEMPLATE.COLLABORATIVE_CARE_MODEL,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  PSYCHIATRIC_PSYCHOTHERAPY: [
    GROUP_NOTE_TEMPLATE.PSYCHIATRIC_PROGRESS_EXAM,
    GROUP_NOTE_TEMPLATE.PSYCHOTHERAPY_NOTE,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
  PSYCHIATRIC_PSYCHOEDUCATION: [
    GROUP_NOTE_TEMPLATE.PSYCHIATRIC_PROGRESS_EXAM,
    GROUP_NOTE_TEMPLATE.PSYCHOEDUCATION_THERAPY,
    GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
  ],
} satisfies Record<string, GroupNoteTemplate[]>;

/** The therapy category each group type is documented under. */
export const GROUP_TYPE_THERAPY_CATEGORY: Record<GroupType, TherapyCategory> = {
  [GROUP_TYPE.CBT_GROUP]:
    THERAPY_CATEGORY.COGNITIVE_BEHAVIORAL_THERAPY_PSYCHOTHERAPY,
  [GROUP_TYPE.DBT_GROUP]:
    THERAPY_CATEGORY.DIALECTICAL_BEHAVIOR_THERAPY_PSYCHOTHERAPY,
  [GROUP_TYPE.ACT_GROUP]:
    THERAPY_CATEGORY.ACCEPTANCE_AND_COMMITMENT_THERAPY_PSYCHOTHERAPY,
  [GROUP_TYPE.TRAUMA_FOCUSED_GROUP]:
    THERAPY_CATEGORY.TRAUMA_FOCUSED_PSYCHOTHERAPY,
  [GROUP_TYPE.GROUP_PSYCHOTHERAPY]: THERAPY_CATEGORY.GROUP_PSYCHOTHERAPY,
  [GROUP_TYPE.SUPPORTIVE_THERAPY_GROUP]: THERAPY_CATEGORY.GROUP_PSYCHOTHERAPY,
  [GROUP_TYPE.BEHAVIORAL_THERAPY_GROUP]:
    THERAPY_CATEGORY.BEHAVIORAL_PSYCHOTHERAPY,
  [GROUP_TYPE.DEPRESSION_SUPPORT_GROUP]:
    THERAPY_CATEGORY.SUPPORTIVE_PSYCHOTHERAPY,
  [GROUP_TYPE.ANXIETY_SUPPORT_GROUP]: THERAPY_CATEGORY.SUPPORTIVE_PSYCHOTHERAPY,
  [GROUP_TYPE.GRIEF_AND_LOSS_GROUP]:
    THERAPY_CATEGORY.SUPPORTIVE_SLASH_PSYCHOTHERAPY,
  [GROUP_TYPE.RECOVERY_SUPPORT_GROUP]:
    THERAPY_CATEGORY.BEHAVIORAL_HEALTH_PSYCHOTHERAPY,
  [GROUP_TYPE.MINDFULNESS_GROUP]: THERAPY_CATEGORY.SKILLS_BASED_THERAPY,
  [GROUP_TYPE.EMOTIONAL_REGULATION_GROUP]:
    THERAPY_CATEGORY.SKILLS_BASED_THERAPY_DBT,
  [GROUP_TYPE.DISTRESS_TOLERANCE_GROUP]:
    THERAPY_CATEGORY.DBT_SKILLS_BASED_THERAPY,
  [GROUP_TYPE.INTERPERSONAL_EFFECTIVENESS_GROUP]:
    THERAPY_CATEGORY.DBT_SKILLS_BASED_THERAPY,
  [GROUP_TYPE.COPING_SKILLS_GROUP]: THERAPY_CATEGORY.SKILLS_BASED_THERAPY,
  [GROUP_TYPE.STRESS_MANAGEMENT_GROUP]:
    THERAPY_CATEGORY.PSYCHOEDUCATION_SKILLS_BASED_THERAPY,
  [GROUP_TYPE.ANGER_MANAGEMENT_GROUP]:
    THERAPY_CATEGORY.PSYCHOEDUCATION_BEHAVIORAL_THERAPY,
  [GROUP_TYPE.SLEEP_MANAGEMENT_GROUP]: THERAPY_CATEGORY.PSYCHOEDUCATION,
  [GROUP_TYPE.MEDICATION_EDUCATION_GROUP]:
    THERAPY_CATEGORY.PSYCHOEDUCATION_PSYCHIATRY,
  [GROUP_TYPE.MENTAL_HEALTH_EDUCATION_GROUP]: THERAPY_CATEGORY.PSYCHOEDUCATION,
  [GROUP_TYPE.RELAPSE_PREVENTION_GROUP]:
    THERAPY_CATEGORY.PSYCHOEDUCATION_BEHAVIORAL_THERAPY,
  [GROUP_TYPE.FAMILY_PSYCHOEDUCATION_GROUP]:
    THERAPY_CATEGORY.FAMILY_THERAPY_PSYCHOEDUCATION,
  [GROUP_TYPE.CAREGIVER_SUPPORT_GROUP]:
    THERAPY_CATEGORY.FAMILY_CAREGIVER_SUPPORT,
  [GROUP_TYPE.FAMILY_THERAPY_GROUP]:
    THERAPY_CATEGORY.FAMILY_THERAPY_PSYCHOTHERAPY,
  [GROUP_TYPE.COCM_GROUP]: THERAPY_CATEGORY.COLLABORATIVE_CARE_MODEL,
  [GROUP_TYPE.BHI_GROUP]: THERAPY_CATEGORY.BEHAVIORAL_HEALTH_INTEGRATION,
  [GROUP_TYPE.PSYCHIATRIC_SKILLS_GROUP]:
    THERAPY_CATEGORY.PSYCHIATRY_BEHAVIORAL_THERAPY,
  [GROUP_TYPE.SYMPTOM_MANAGEMENT_GROUP]:
    THERAPY_CATEGORY.PSYCHIATRY_PSYCHOEDUCATION,
  [GROUP_TYPE.MEDICATION_ADHERENCE_GROUP]:
    THERAPY_CATEGORY.PSYCHIATRY_PSYCHOEDUCATION,
};

/** Visit note templates each group type supports, in the order offered. */
export const GROUP_TYPE_NOTE_TEMPLATES: Record<GroupType, GroupNoteTemplate[]> =
  {
    [GROUP_TYPE.CBT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.DBT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.ACT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.TRAUMA_FOCUSED_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.GROUP_PSYCHOTHERAPY]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.SUPPORTIVE_THERAPY_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.BEHAVIORAL_THERAPY_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.DEPRESSION_SUPPORT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.ANXIETY_SUPPORT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.GRIEF_AND_LOSS_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.RECOVERY_SUPPORT_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.MINDFULNESS_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.EMOTIONAL_REGULATION_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.DISTRESS_TOLERANCE_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.INTERPERSONAL_EFFECTIVENESS_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.COPING_SKILLS_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.STRESS_MANAGEMENT_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHOTHERAPY,
    [GROUP_TYPE.ANGER_MANAGEMENT_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHOTHERAPY,
    [GROUP_TYPE.SLEEP_MANAGEMENT_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION,
    [GROUP_TYPE.MEDICATION_EDUCATION_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHIATRIC,
    [GROUP_TYPE.MENTAL_HEALTH_EDUCATION_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION,
    [GROUP_TYPE.RELAPSE_PREVENTION_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHOTHERAPY,
    [GROUP_TYPE.FAMILY_PSYCHOEDUCATION_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHOTHERAPY,
    [GROUP_TYPE.CAREGIVER_SUPPORT_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHOEDUCATION_PSYCHOTHERAPY,
    [GROUP_TYPE.FAMILY_THERAPY_GROUP]: GROUP_NOTE_TEMPLATE_SET.FULL,
    [GROUP_TYPE.COCM_GROUP]: GROUP_NOTE_TEMPLATE_SET.COLLABORATIVE_CARE,
    [GROUP_TYPE.BHI_GROUP]: GROUP_NOTE_TEMPLATE_SET.COLLABORATIVE_CARE,
    [GROUP_TYPE.PSYCHIATRIC_SKILLS_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHIATRIC_PSYCHOTHERAPY,
    [GROUP_TYPE.SYMPTOM_MANAGEMENT_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHIATRIC_PSYCHOEDUCATION,
    [GROUP_TYPE.MEDICATION_ADHERENCE_GROUP]:
      GROUP_NOTE_TEMPLATE_SET.PSYCHIATRIC_PSYCHOEDUCATION,
  };

/** Offered when the group carries a type the table does not list. */
export const GROUP_NOTE_DEFAULT_TEMPLATES: GroupNoteTemplate[] = [
  GROUP_NOTE_TEMPLATE.PROGRESS_NOTE,
];

export const SESSION_DURATION_OPTIONS: Option[] = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "45 minutes", label: "45 minutes" },
  { value: "60 minutes", label: "60 minutes" },
];

export const SESSION_FREQUENCY = {
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  CUSTOM: "Custom",
} as const;

export const SESSION_FREQUENCY_OPTIONS: Option[] = [
  { value: SESSION_FREQUENCY.DAILY, label: "Daily" },
  { value: SESSION_FREQUENCY.WEEKLY, label: "Weekly" },
  { value: SESSION_FREQUENCY.MONTHLY, label: "Monthly" },
];

/** Recurrence day, stored as the sentence the API round-trips. */
export const REPEATS_ON_OPTIONS: Option[] = [
  { value: "Every Sunday", label: "Every Sunday" },
  { value: "Every Monday", label: "Every Monday" },
  { value: "Every Tuesday", label: "Every Tuesday" },
  { value: "Every Wednesday", label: "Every Wednesday" },
  { value: "Every Thursday", label: "Every Thursday" },
  { value: "Every Friday", label: "Every Friday" },
  { value: "Every Saturday", label: "Every Saturday" },
];

export const GROUP_FORM_MODE = {
  CREATE: "CREATE",
  EDIT: "EDIT",
} as const;

/** Which create button submitted the drawer: back to the list, or on to the edit screen. */
export const GROUP_SUBMIT_ACTION = {
  SAVE: "SAVE",
  SAVE_AND_NEXT: "SAVE_AND_NEXT",
} as const;

export const GROUP_SECTION = {
  INFORMATION: "group-information",
  SESSION: "session-configuration",
  PROVIDER: "provider-assignment",
  AVAILABILITY: "group-availability",
} as const;

/** Create collects only the group identity; the rest is configured while editing. */
export const GROUP_CREATE_SECTIONS: string[] = [GROUP_SECTION.INFORMATION];

export const GROUP_EDIT_SECTIONS: string[] = [
  GROUP_SECTION.INFORMATION,
  GROUP_SECTION.SESSION,
  GROUP_SECTION.PROVIDER,
  GROUP_SECTION.AVAILABILITY,
];

export const GROUP_SECTION_NAV: ChipSelectorItem[] = [
  { id: GROUP_SECTION.INFORMATION, label: "Group Information" },
  { id: GROUP_SECTION.SESSION, label: "Session Configuration" },
  { id: GROUP_SECTION.PROVIDER, label: "Provider Assignment" },
  { id: GROUP_SECTION.AVAILABILITY, label: "Group Availability" },
];

export const GROUP_LIST_FILTER = {
  ALL: "ALL",
  ACTIVE: GROUP_STATUS.ACTIVE,
  INACTIVE: GROUP_STATUS.INACTIVE,
  ARCHIVED: GROUP_STATUS.ARCHIVED,
} as const;

export const GROUP_LIST_FILTER_CHIPS: ChipSelectorItem[] = [
  { id: GROUP_LIST_FILTER.ALL, label: "All" },
  { id: GROUP_LIST_FILTER.ACTIVE, label: "Active" },
  { id: GROUP_LIST_FILTER.INACTIVE, label: "Inactive" },
  { id: GROUP_LIST_FILTER.ARCHIVED, label: "Archived" },
];

/**
 * MUI pulls a switch 11px left of its row, so a toggle would hang outside the
 * field column; this lines it up and spaces the track off its label.
 */
export const GROUP_SWITCH_SX = { ml: 0, gap: 1 } as const;

/** Search sits above the roster; both share the tab's inset. */
export const MANAGE_PATIENTS_SEARCH_WIDTH = 360;
export const MANAGE_PATIENTS_TABLE_HEIGHT = 420;

/** Readable column width for the edit form; the page itself is much wider. */
export const GROUP_FORM_MAX_WIDTH = 760;

/** The action column's grid field — a click there must not open the group. */
export const GROUP_LIST_ACTION_FIELD = "actions";

/** Grid fields the list can be sorted by; everything else stays static. */
export const GROUP_LIST_SORTABLE_FIELDS = [
  "groupName",
  "primaryTherapistName",
  "groupType",
  "therapyCategory",
  "status",
];

/** Grid field -> the column name `sortBy` expects. */
export const GROUP_LIST_SORT_FIELD_MAP: Record<string, string> = {
  groupName: "group_name",
  primaryTherapistName: "primary_therapist_id",
  groupType: "group_type",
  therapyCategory: "therapy_category",
  status: "group_status",
};

/** Newest first until the user sorts a column. */
export const GROUP_LIST_DEFAULT_SORT_BY = "modified";
export const GROUP_LIST_DEFAULT_SORT_DIRECTION = "desc";

/** Search box keeps a fixed width so the status chips sit beside it. */
export const GROUP_LIST_SEARCH_WIDTH = 232;

export const GROUP_LIST_LABEL = {
  title: "Patients/ Group List",
  backToPatients: "Back to Patient List",
  searchPlaceholder: "Search by group name",
  createGroup: "Create New Group",
  noGroups: "No groups available",
  statusFilterLabel: "Filter groups by status",
  sectionNavLabel: "Jump to a section",
} as const;

export const GROUP_LIST_COLUMN_LABEL = {
  groupName: "Group Name",
  providerName: "Provider Name",
  groupType: "Group Type",
  therapyCategory: "Therapy Category",
  enrolledPatients: "Enrolled Patients",
  minPatientReq: "Min Patient Req",
  status: "Status",
  action: "Action",
} as const;

/** Shown for Enrolled Patients / Min Patient Req before a group is configured. */
export const GROUP_UNCONFIGURED_VALUE = "-";

/** Primary and co-therapist read as one sequential Provider Name cell. */
export const PROVIDER_NAME_SEPARATOR = ", ";

export const GROUP_ROW_ACTION = {
  EDIT: "edit",
  VIEW: "view",
  VIEW_DETAILS: "view-details",
  ARCHIVE: "archive",
  CHANGE_STATUS: "change-status",
} as const;

export const GROUP_ROW_ACTION_LABEL = {
  [GROUP_ROW_ACTION.VIEW_DETAILS]: "View Group Details",
  [GROUP_ROW_ACTION.VIEW]: "Complete Group Visit",
  [GROUP_ROW_ACTION.EDIT]: "Edit Group Details",
  [GROUP_ROW_ACTION.ARCHIVE]: "Archive",
  [GROUP_ROW_ACTION.CHANGE_STATUS]: "Change Status",
} as const;

/** Change Status menu label, keyed by the status the group will move to. */
export const GROUP_STATUS_TOGGLE_LABEL: Record<string, string> = {
  [GROUP_STATUS.ACTIVE]: "Active Group",
  [GROUP_STATUS.INACTIVE]: "Inactive Group",
};

export const GROUP_ROW_ACTION_HINT = {
  archiveUnavailable: "Only an inactive group can be archived",
  archivedReadOnly: "An archived group cannot be edited",
} as const;

export const GROUP_DETAIL_TAB = {
  DETAILS: "group-details",
  THERAPISTS: "manage-therapists",
  PATIENTS: "manage-patients",
  NOTIFICATIONS: "notifications-reminders",
} as const;

/** Tab a group opens on when the URL names none. */
export const GROUP_DETAIL_DEFAULT_TAB = GROUP_DETAIL_TAB.DETAILS;

export const GROUP_DETAIL_TABS = [
  { value: GROUP_DETAIL_TAB.DETAILS, label: "Group Details" },
  { value: GROUP_DETAIL_TAB.THERAPISTS, label: "Manage Therapists" },
  { value: GROUP_DETAIL_TAB.PATIENTS, label: "Manage Patients" },
  { value: GROUP_DETAIL_TAB.NOTIFICATIONS, label: "Notifications" },
];

/** Tabs a URL may open; a hidden tab falls back to the default. */
export const GROUP_DETAIL_TAB_VALUES: string[] = GROUP_DETAIL_TABS.map(
  (tab) => tab.value
);

export const GROUP_DETAIL_LABEL = {
  backToGroups: "Back to Group List",
  appointmentTag: "Group Appointment",
  editGroupDetails: "Edit Group Details",
  informationCard: "Group information",
  careTeamCard: "Care team",
  enrollmentCard: "Enrollment",
  nextSession: "Next Session",
  noNextSession: "No upcoming session scheduled",
  enrolledPatients: (count: number) =>
    `${count} ${count === 1 ? "patient" : "patients"} enrolled`,
  quickStats: "Quick stats",
  primaryTherapist: "Primary therapist",
  coTherapist: "Co-therapist",
  tabPlaceholder: "This section is not available yet.",
} as const;

export const GROUP_DETAIL_FIELD_LABEL = {
  groupName: "Group Name",
  typeCategory: "Type · Category",
  capacity: "Capacity",
  description: "Description",
} as const;

/** Audit-log query behind the Therapist change history panel. */
export const THERAPIST_HISTORY_LOG_TYPE = "Group Therapist";
export const THERAPIST_HISTORY_SORT_BY = "log_time";
export const THERAPIST_HISTORY_SORT_DIRECTION = "DESC";
export const THERAPIST_HISTORY_PAGE_SIZE = 10;

/** Caps the change history so it scrolls in place instead of growing the page. */
export const THERAPIST_HISTORY_MAX_HEIGHT = 320;

export const GROUP_THERAPIST_LABEL = {
  primaryTherapist: "Primary Therapist",
  coTherapists: "Co-Therapists / Facilitators",
  addCoTherapist: "Add co-therapist",
  changeHistory: "Therapist change history",
  auditLog: "audit log",
  noCoTherapists:
    "No co-therapists yet — optional. Add one from the provider directory.",
  noHistory: "No changes recorded yet.",
  loadingHistory: "Loading changes…",
  noPrimaryTherapist: "No primary therapist assigned yet.",
} as const;

export const GROUP_VISIT_TIME_FORMAT = "hh:mm A";
export const GROUP_VISIT_DATE_TIME_FORMAT = `${DATE_FORMAT}, ${GROUP_VISIT_TIME_FORMAT}`;
export const GROUP_HISTORY_DATE_FORMAT = GROUP_VISIT_DATE_TIME_FORMAT;

export const GROUP_THERAPIST_FIELD_LABEL = {
  specialty: "Specialty",
  role: "Role",
  location: "Location",
  email: "Email",
} as const;

export const SESSION_STATUS = {
  SCHEDULED: "SCHEDULED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const;

export const SESSION_STATUS_LABEL: Record<string, string> = {
  [SESSION_STATUS.SCHEDULED]: "Scheduled",
  [SESSION_STATUS.COMPLETED]: "Completed",
  [SESSION_STATUS.CANCELLED]: "Cancelled",
};

/** Placeholder rooms until the practice location API is wired in. */
export const GROUP_LOCATION_OPTIONS: Option[] = [
  { value: "ROOM_2B", label: "Room 2B" },
  { value: "ROOM_3A", label: "Room 3A" },
  { value: "TELEHEALTH", label: "Telehealth" },
];

export const SESSION_VISIT_TYPE = {
  IN_PERSON: "IN_PERSON",
  TELEHEALTH: "VIRTUAL",
} as const;

export const SESSION_VISIT_TYPE_OPTIONS: Option[] = [
  { value: SESSION_VISIT_TYPE.IN_PERSON, label: "In Person" },
  { value: SESSION_VISIT_TYPE.TELEHEALTH, label: "Telehealth" },
];

export const SESSION_REPEAT_OPTIONS: Option[] = [
  { value: "1", label: "1 session (one-off)" },
  { value: "4", label: "4 sessions" },
  { value: "8", label: "8 sessions" },
  { value: "12", label: "12 sessions" },
];

/** Appointments list cache the scheduled group sessions also show in. */
export const APPOINTMENT_LIST_QUERY_KEY = "appointment-list-v2";

export const SESSION_DATE_FORMAT = DATE_FORMAT;
export const SESSION_TIME_FORMAT = GROUP_VISIT_TIME_FORMAT;

export const NOTIFICATION_CHANNEL = {
  PORTAL: "PORTAL",
  EMAIL: "EMAIL",
  SMS: "SMS",
} as const;

export const NOTIFICATION_CHANNEL_ITEMS = [
  { id: NOTIFICATION_CHANNEL.PORTAL, label: "Patient portal" },
  { id: NOTIFICATION_CHANNEL.EMAIL, label: "Email" },
  { id: NOTIFICATION_CHANNEL.SMS, label: "SMS" },
];

export const REMINDER_SCHEDULE = {
  ON_CONFIRMATION: "ON_CONFIRMATION",
  TWENTY_FOUR_HOURS: "TWENTY_FOUR_HOURS",
  ONE_HOUR: "ONE_HOUR",
} as const;

export const REMINDER_SCHEDULE_ITEMS = [
  // { id: REMINDER_SCHEDULE.ON_CONFIRMATION, label: "On confirmation" },
  { id: REMINDER_SCHEDULE.TWENTY_FOUR_HOURS, label: "24 hours before" },
  { id: REMINDER_SCHEDULE.ONE_HOUR, label: "1 hour before" },
];

/** Toggle id → the `GroupVisit` boolean it reads and writes. */
export const NOTIFICATION_CHANNEL_FIELD = {
  [NOTIFICATION_CHANNEL.PORTAL]: "patientPortalNotification",
  [NOTIFICATION_CHANNEL.EMAIL]: "emailNotification",
  [NOTIFICATION_CHANNEL.SMS]: "smsNotification",
} as const;

export const REMINDER_SCHEDULE_FIELD = {
  [REMINDER_SCHEDULE.ON_CONFIRMATION]: "onConfirmationReminder",
  [REMINDER_SCHEDULE.TWENTY_FOUR_HOURS]: "twentyFourHoursBeforeReminder",
  [REMINDER_SCHEDULE.ONE_HOUR]: "oneHourBeforeReminder",
} as const;

export const NOTIFICATION_SECTION = {
  CHANNELS: "channels",
  REMINDERS: "reminders",
} as const;

export const NOTIFICATION_CARD_WIDTH = 430;

/** Placeholder templates until the message-template API is wired in. */
export const MESSAGE_TEMPLATE_OPTIONS: Option[] = [
  { value: "GROUP_SESSION_CONFIRMATION", label: "Group session confirmation" },
  { value: "GROUP_SESSION_REMINDER", label: "Group session reminder" },
  { value: "GROUP_SESSION_CANCELLATION", label: "Group session cancellation" },
];

export const NOTIFICATION_LABEL = {
  channelsCard: "Channels",
  reminderScheduleCard: "Reminder schedule",
  messageTemplateCard: "Message template",
} as const;

export const NOTIFICATION_SETTINGS_INITIAL_VALUES = {
  channels: {
    [NOTIFICATION_CHANNEL.PORTAL]: true,
    [NOTIFICATION_CHANNEL.EMAIL]: true,
    [NOTIFICATION_CHANNEL.SMS]: true,
  },
  reminders: {
    [REMINDER_SCHEDULE.ON_CONFIRMATION]: true,
    [REMINDER_SCHEDULE.TWENTY_FOUR_HOURS]: true,
    [REMINDER_SCHEDULE.ONE_HOUR]: false,
  },
  templateId: MESSAGE_TEMPLATE_OPTIONS[0].value,
};

export const PATIENT_ENROLLMENT_STATUS = {
  ACTIVE: "ACTIVE",
  PENDING: "PENDING",
  ON_HOLD: "ON_HOLD",
  WAITLIST: "WAITLIST",
} as const;

export const PATIENT_ENROLLMENT_STATUS_LABEL: Record<string, string> = {
  [PATIENT_ENROLLMENT_STATUS.ACTIVE]: "Active",
  [PATIENT_ENROLLMENT_STATUS.PENDING]: "Pending",
  [PATIENT_ENROLLMENT_STATUS.ON_HOLD]: "On hold",
  [PATIENT_ENROLLMENT_STATUS.WAITLIST]: "Waitlist",
};

export const PATIENT_LIST_FILTER = {
  ALL: "ALL",
  ACTIVE: PATIENT_ENROLLMENT_STATUS.ACTIVE,
  PENDING: PATIENT_ENROLLMENT_STATUS.PENDING,
  WAITLIST: PATIENT_ENROLLMENT_STATUS.WAITLIST,
} as const;

export const PATIENT_LIST_FILTER_LABEL: Record<string, string> = {
  [PATIENT_LIST_FILTER.ALL]: "All",
  [PATIENT_LIST_FILTER.ACTIVE]: "Active",
  [PATIENT_LIST_FILTER.PENDING]: "Pending",
  [PATIENT_LIST_FILTER.WAITLIST]: "Waitlist",
};

export const MANAGE_PATIENTS_LABEL = {
  enrolledAllowed: "Enrolled/Allowed",
  waitlist: "Waitlist",
  editPatientList: "Edit Patient list",
  removeSelected: "Remove Selected",
  addPatients: "Add Patients",
  searchPlaceholder: "Search",
  filter: "Filter",
  noPatients: "No patients enrolled yet.",
  attendanceEmpty: "—",
} as const;

export const PATIENT_COLUMN_LABEL = {
  patient: "PATIENT",
  mrn: "Patient ID",
  dob: "DOB",
  paymentMode: "Payment Mode",
  enrolled: "ENROLLED",
  status: "STATUS",
  attendance: "ATTENDANCE",
} as const;

export const PATIENT_ROW_ACTION = {
  // VIEW_CHART: "view-chart",
  // MOVE_TO_WAITLIST: "move-to-waitlist",
  REMOVE: "remove",
} as const;

export const PATIENT_ROW_ACTION_OPTIONS = [
  // { value: PATIENT_ROW_ACTION.VIEW_CHART, label: "View Patient Chart" },
  // { value: PATIENT_ROW_ACTION.MOVE_TO_WAITLIST, label: "Move to Waitlist" },
  { value: PATIENT_ROW_ACTION.REMOVE, label: "Remove Patient" },
];

export const ADD_PATIENT_LABEL = {
  title: "Add Patient to Group",
  submit: "Add Patients to Group",
  searchField: "Search & Add Patient",
  placeholder: "Search patient by name",
  required: "Select at least one patient",
} as const;

export const ADD_PATIENT_DRAWER_WIDTH = "42vw";

export const PATIENT_DOB_FORMAT = DATE_FORMAT;
export const PATIENT_ENROLLED_DATE_FORMAT = DATE_FORMAT;

export const GROUP_STAT_LABEL = {
  sessionsHeld: "Sessions held",
  attendance: "Attendance",
  notesDue: "Notes due",
} as const;

export const GROUP_ENROLLMENT_TEXT = {
  waitlistSuffix: "waitlist",
  memberOf: "of",
} as const;

export const GROUP_NEXT_SESSION_DATE_FORMAT = GROUP_VISIT_DATE_TIME_FORMAT;

export const GROUP_LABEL = {
  informationSection: "Group Name Details",
  sessionSection: "Session Configuration",
  providerSection: "Provider Assignment",
  availabilitySection: "Group Availability",
  groupName: "Group Name",
  groupType: "Group Type",
  therapyCategory: "Therapy Category",
  amountForPatient: "Amount For Patient",
  description: "Group Description",
  sessionDuration: "Default Session Duration",
  sessionFrequency: "Session Frequency",
  customDays: "Session Days",
  maxCapacity: "Maximum Capacity",
  minToRun: "Minimum To Run",
  primaryTherapist: "Primary Therapist",
  coTherapist: "Co-Therapist / Facilitator (Optional)",
  startDate: "Start Date",
  endDate: "End Date",
  recurringSchedule: "Recurring Schedule",
  repeatsOn: "Repeats On",
} as const;

export const GROUP_PLACEHOLDER = {
  groupName: "Enter Group Name",
} as const;

export const GROUP_HELPER_TEXT = {
  groupName:
    "A unique, patient-facing name that identifies this therapy group.",
  amountForPatient: "Amount charged to the patient for the complete session.",
  minToRun: "Minimum enrolled patients required to run a session.",
  recurringSchedule: "Generate a session series automatically",
} as const;

export const GROUP_TITLE = {
  [GROUP_FORM_MODE.CREATE]: "Create Group",
  [GROUP_FORM_MODE.EDIT]: "Edit Group Details",
} as const;

export const GROUP_VIEW_TITLE = "View Group Details";

export const GROUP_ACTION_LABEL = {
  save: "Save Details",
  create: "Save Group",
  saveAndNext: "Save & Next",
  cancel: "Cancel",
  close: "Close",
  archive: "Archive Group",
} as const;

export const GROUP_CONFIRMATION = {
  discardTitle: "Discard changes?",
  discardContent:
    "You have unsaved changes. Closing this form will discard them.",
  discardConfirm: "Discard",
  archiveTitle: "Archive this group?",
  archiveContent:
    "Archiving keeps historical records viewable but stops further scheduling and enrollment.",
  archiveConfirm: "Archive",
  inactivateTitle: "Inactivate this group?",
  inactivateContent:
    "Inactivating this group will cancel all of its upcoming appointments and release the booked slots. If the group is reactivated later, those appointments stay cancelled and must be booked again.",
  inactivateConfirm: "Cancel Appointments & Inactivate",
} as const;

export const REMOVE_PATIENT_SESSIONS_LABEL = {
  title: "Remove patient from group?",
  bulkTitle: "Remove selected patients from group?",
  content:
    "This patient may be booked into upcoming sessions of this group. Choose whether to keep those bookings or cancel them.",
  bulkContent:
    "These patients may be booked into upcoming sessions of this group. Choose whether to keep those bookings or cancel them.",
  pastSessionsNote: "Past and completed sessions are not changed.",
  back: "Back",
  keepSessions: "Keep Scheduled Sessions",
  cancelSessions: "Cancel Scheduled Sessions",
} as const;

export const GROUP_MESSAGE = {
  createSuccess: "Group created successfully",
  updateSuccess: "Group details updated successfully",
  archiveSuccess: "Group archived successfully",
  statusChangeSuccess: "Group status updated successfully",
  sessionScheduled: "Session scheduled successfully",
  sessionCancelled: "Session cancelled successfully",
  patientsAdded: "Patients added to the group",
  patientUpdated: "Patient enrollment updated",
  saveError: "Unable to save group details. Please try again.",
  loadingGroup: "Loading group details...",
  loadGroupError: "Unable to load this group. Please try again.",
} as const;

export const GROUP_CAPACITY = {
  min: 1,
  max: 999,
  step: 1,
} as const;

/** A session needs at least two patients to run as a group. */
export const GROUP_MIN_TO_RUN_MIN = 2;

export const GROUP_AMOUNT = {
  min: 0,
  max: 999999,
  currencySymbol: "$",
  decimalScale: 2,
} as const;

export const GROUP_DESCRIPTION_ROWS = 4;
export const GROUP_DESCRIPTION_MAX_CHARS = 1000;
export const GROUP_DRAWER_WIDTH = "42vw";
export const GROUP_NAME_MAX_CHARS = 100;
export const GROUP_NAME_ALLOWED_CHARS_REGEX = /^[A-Za-z0-9 ]+$/;
export const GROUP_NAME_LETTER_REGEX = /[A-Za-z]/;

export const GROUP_VALIDATION_MESSAGE = {
  groupNameRequired: "Group Name is required",
  groupNameBlank: "Group Name cannot be only spaces",
  groupNameDuplicate: "A group with this name already exists",
  groupNameInvalidChars:
    "Group Name can contain only letters, numbers and spaces",
  groupNameNoLetter: "Group Name must contain at least one letter",
  groupNameMax: `Group Name cannot exceed ${GROUP_NAME_MAX_CHARS} characters`,
  groupTypeRequired: "Group Type is required",
  therapyCategoryRequired: "Therapy Category is required",
  amountInvalid: "Enter a valid amount",
  amountNegative: "Amount cannot be negative",
  descriptionMax: `Description cannot exceed ${GROUP_DESCRIPTION_MAX_CHARS} characters`,
  sessionDurationRequired: "Default Session Duration is required",
  maxCapacityRequired: "Maximum Capacity is required",
  maxCapacityInvalid: "Maximum Capacity must be a whole number above zero",
  maxCapacityBelowEnrolled:
    "Maximum Capacity cannot be less than the currently enrolled patients",
  minToRunInvalid: "Minimum To Run must be a whole number above zero",
  minToRunBelowMin: `Minimum To Run must be at least ${GROUP_MIN_TO_RUN_MIN}`,
  minToRunAboveMax: "Minimum To Run cannot exceed Maximum Capacity",
  // minToRunAboveEnrolled: (minToRun: number) =>
  //   `Minimum ${minToRun} patients required to run the session`,
  primaryTherapistRequired: "Primary Therapist is required",
  coTherapistSameAsPrimary:
    "Co-Therapist cannot be the same as the Primary Therapist",
  startDateRequired: "Start Date is required",
  slotRequired: "Please select an available slot",
  locationRequired: "Location is required",
  endDateBeforeStart: "End Date cannot be earlier than Start Date",
  repeatsOnRequired: "Select a day for the recurring schedule",
} as const;

/** Same values the individual Schedule Appointment form sends. */
export const RECURRING_FREQUENCY = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  CUSTOM: "custom",
} as const satisfies Record<string, RecurringFrequency>;

export const RECURRING_END_TYPE = {
  NEVER: "never",
  ON_DATE: "on_date",
  AFTER_N_EVENTS: "after_n_events",
} as const satisfies Record<string, EndType>;

export const CUSTOM_FREQUENCY_UNIT = {
  DAYS: "days",
  WEEKS: "weeks",
  MONTHS: "months",
} as const satisfies Record<string, CustomFrequencyUnit>;

/** Group record's `sessionFrequency` sentence onto the recurring frequency value. */
export const SESSION_FREQUENCY_TO_RECURRING: Record<
  SessionFrequency,
  RecurringFrequency
> = {
  [SESSION_FREQUENCY.DAILY]: RECURRING_FREQUENCY.DAILY,
  [SESSION_FREQUENCY.WEEKLY]: RECURRING_FREQUENCY.WEEKLY,
  [SESSION_FREQUENCY.MONTHLY]: RECURRING_FREQUENCY.MONTHLY,
  [SESSION_FREQUENCY.CUSTOM]: RECURRING_FREQUENCY.CUSTOM,
};

export const GROUP_RECURRING_DEFAULTS = {
  recurringFrequency: RECURRING_FREQUENCY.DAILY,
  endType: RECURRING_END_TYPE.ON_DATE,
  endAfter: 5,
  customFrequency: 1,
  customFrequencyUnit: CUSTOM_FREQUENCY_UNIT.DAYS,
} as const;

export const GROUP_FORM_INITIAL_VALUES: GroupFormValues = {
  groupName: "",
  groupType: null,
  therapyCategory: null,
  amountForPatient: "",
  description: "",
  // A new group is inactive until it is activated from Group Configuration.
  status: GROUP_STATUS.INACTIVE,

  sessionDurationMinutes: null,
  sessionFrequency: null,
  customDays: [],
  maxCapacity: "",
  minToRun: String(GROUP_MIN_TO_RUN_MIN),

  primaryTherapist: null,
  coTherapist: null,

  startDate: null,
  isRecurring: false,
  recurringFrequency: GROUP_RECURRING_DEFAULTS.recurringFrequency,
  endType: GROUP_RECURRING_DEFAULTS.endType,
  endDate: null,
  endAfter: GROUP_RECURRING_DEFAULTS.endAfter,
  customFrequency: GROUP_RECURRING_DEFAULTS.customFrequency,
  customFrequencyUnit: GROUP_RECURRING_DEFAULTS.customFrequencyUnit,
  selectedWeekdays: [],
};

const WHOLE_NUMBER_REGEX = /^\d+$/;

export type GroupSchemaContext = {
  /** Names already used by other groups, lower-cased, for the duplicate check. */
  existingGroupNames: string[];
  enrolledPatientCount?: number;
  // /** Editing a saved group, whose enrolled patients Minimum To Run is checked against. */
  // isExistingGroup?: boolean;
  /** Sections on screen — only their fields are validated. */
  sections?: string[];
};

const getInformationSchemaFields = (existingGroupNames: string[]) => ({
  groupName: Yup.string()
    .required(GROUP_VALIDATION_MESSAGE.groupNameRequired)
    .max(GROUP_NAME_MAX_CHARS, GROUP_VALIDATION_MESSAGE.groupNameMax)
    .test(
      "not-blank",
      GROUP_VALIDATION_MESSAGE.groupNameBlank,
      (value) => !!value?.trim()
    )
    .test(
      "allowed-chars",
      GROUP_VALIDATION_MESSAGE.groupNameInvalidChars,
      (value) =>
        !value?.trim() || GROUP_NAME_ALLOWED_CHARS_REGEX.test(value.trim())
    )
    .test(
      "has-letter",
      GROUP_VALIDATION_MESSAGE.groupNameNoLetter,
      (value) => !value?.trim() || GROUP_NAME_LETTER_REGEX.test(value)
    )
    .test(
      "not-duplicate",
      GROUP_VALIDATION_MESSAGE.groupNameDuplicate,
      (value) =>
        !value || !existingGroupNames.includes(value.trim().toLowerCase())
    ),
  groupType: Yup.string()
    .nullable()
    .required(GROUP_VALIDATION_MESSAGE.groupTypeRequired),
  therapyCategory: Yup.string()
    .nullable()
    .required(GROUP_VALIDATION_MESSAGE.therapyCategoryRequired),
  amountForPatient: Yup.string().test(
    "valid-amount",
    GROUP_VALIDATION_MESSAGE.amountInvalid,
    (value) => !value || (Number.isFinite(+value) && +value >= GROUP_AMOUNT.min)
  ),
  description: Yup.string().max(
    GROUP_DESCRIPTION_MAX_CHARS,
    GROUP_VALIDATION_MESSAGE.descriptionMax
  ),
});

const getConfigurationSchemaFields = (
  enrolledPatientCount: number
  // isExistingGroup: boolean
) => ({
  sessionDurationMinutes: Yup.string()
    .nullable()
    .required(GROUP_VALIDATION_MESSAGE.sessionDurationRequired),
  maxCapacity: Yup.string()
    .required(GROUP_VALIDATION_MESSAGE.maxCapacityRequired)
    .test(
      "whole-number",
      GROUP_VALIDATION_MESSAGE.maxCapacityInvalid,
      (value) => WHOLE_NUMBER_REGEX.test(value ?? "") && +(value ?? 0) > 0
    )
    .test(
      "not-below-enrolled",
      GROUP_VALIDATION_MESSAGE.maxCapacityBelowEnrolled,
      (value) => !value || +value >= enrolledPatientCount
    ),
  minToRun: Yup.string()
    .test(
      "whole-number",
      GROUP_VALIDATION_MESSAGE.minToRunInvalid,
      (value) => !value || (WHOLE_NUMBER_REGEX.test(value) && +value > 0)
    )
    .test(
      "not-below-min",
      GROUP_VALIDATION_MESSAGE.minToRunBelowMin,
      (value) => !value || +value >= GROUP_MIN_TO_RUN_MIN
    )
    .test(
      "not-above-max",
      GROUP_VALIDATION_MESSAGE.minToRunAboveMax,
      function (value) {
        const maxCapacity = this.parent.maxCapacity;
        if (!value || !maxCapacity) return true;
        return +value <= +maxCapacity;
      }
    ),
  // .test("not-above-enrolled", function (value) {
  //   if (!isExistingGroup || !value || +value <= enrolledPatientCount) {
  //     return true;
  //   }
  //   return this.createError({
  //     message: GROUP_VALIDATION_MESSAGE.minToRunAboveEnrolled(+value),
  //   });
  // }),
  primaryTherapist: Yup.object()
    .nullable()
    .required(GROUP_VALIDATION_MESSAGE.primaryTherapistRequired),
  coTherapist: Yup.object()
    .nullable()
    .test(
      "not-primary",
      GROUP_VALIDATION_MESSAGE.coTherapistSameAsPrimary,
      function (value) {
        const primary = this.parent
          .primaryTherapist as AsyncSelectOption | null;
        const coTherapist = value as AsyncSelectOption | null;
        if (!coTherapist || !primary) return true;
        return coTherapist.value !== primary.value;
      }
    ),
  startDate: Yup.mixed()
    .nullable()
    .required(GROUP_VALIDATION_MESSAGE.startDateRequired),
  // ...RECURRING_SCHEMA_FIELDS,
});

const CONFIGURATION_SECTIONS: string[] = [
  GROUP_SECTION.SESSION,
  GROUP_SECTION.PROVIDER,
  GROUP_SECTION.AVAILABILITY,
];

export const getGroupValidationSchema = ({
  existingGroupNames,
  enrolledPatientCount = 0,
  // isExistingGroup = false,
  sections = GROUP_CREATE_SECTIONS,
}: GroupSchemaContext) => {
  const informationFields = getInformationSchemaFields(existingGroupNames);

  const hasConfiguration = sections.some((section) =>
    CONFIGURATION_SECTIONS.includes(section)
  );

  if (!hasConfiguration) return Yup.object(informationFields);

  return Yup.object({
    ...informationFields,
    ...getConfigurationSchemaFields(enrolledPatientCount),
  });
};

export const GROUP_FIELDS_SECTION = {
  DATE_TIME: "Date & Time",
  CAPACITY: "Session Capacity",
  THERAPIST: "Therapist & Co-facilitator",
} as const;

export const GROUP_FIELDS_LABEL = {
  groupName: "Group Name",
  startDate: "Start Date",
  sessionDuration: "Default Session Duration",
  recurringSchedule: "Recurring Schedule",
  maxCapacity: "Max Capacity",
  minToRun: "Min to run",
  therapist: "Therapist",
  coFacilitator: "Co-Facilitator",
  visitType: "Visit Type:",
  location: "Location",
  sendForms: "Forms will be emailed",
  intakeForms: "Intake Forms",
  assessments: "Assessments",
} as const;

export const GROUP_FORMS_LIST_STYLE = { listStyleType: "square" } as const;
export const GROUP_FORMS_ICON_STYLE = { fontSize: 18 } as const;

export const VISIT_MODE = {
  IN_PERSON: "IN_PERSON",
  VIRTUAL: "VIRTUAL",
} as const satisfies Record<string, GroupVisitSessionModeEnum>;

export const GROUP_SLOT_LABEL = {
  viewAvailability: "View availability",
  date: "Date",
  time: "Time",
  completeVisitFirst:
    "Select a group, appointment type and location (In Person) first",
  selectDate: "Please Select Date",
  loading: "Loading slots…",
  noSlots: "No Slots Available",
} as const;

/** Calendar-day key the slot search is cached on. */

export const SLOT_PICKER_SX = {
  width: "45rem",
  listHeight: "300px",
  slotWidth: "48%",
  buttonHeight: "34px",
  fieldWidth: "100%",
} as const;

export const SLOT_TIME_FORMAT = "hh:mm A";

export const GROUP_FIELDS_PLACEHOLDER = {
  date: "MM/DD/YYYY",
} as const;

export const GROUP_FIELDS_VALIDATION_MESSAGE = {
  groupNameRequired: "Group Name is required",
  startDateRequired: "Start Date is required",
  slotRequired: "Please select an available slot",
  locationRequired: "Location is required",
  recurringFrequencyRequired: "Frequency is required",
  endTypeRequired: "End type is required",
  endDateRequired: "End On is required",
  endAfterRequired: "End after these number is required",
  endAfterInvalid: "End after must be a whole number above zero",
  customFrequencyRequired: "Repeat every is required",
  customFrequencyInvalid: "Repeat every must be a whole number above zero",
  customFrequencyUnitRequired: "Repeat every unit is required",
  selectedWeekdaysRequired: "Select at least one day to recur on",
} as const;

export const GROUP_FIELDS_INITIAL_VALUES: GroupFieldsValues = {
  groupId: null,
  startDate: null,
  startTime: null,
  endTime: null,
  sessionDurationMinutes: null,
  providerId: null,

  mode: VISIT_MODE.IN_PERSON,
  locationId: null,

  isRecurring: true,
  recurringFrequency: GROUP_RECURRING_DEFAULTS.recurringFrequency,
  endType: GROUP_RECURRING_DEFAULTS.endType,
  endDate: null,
  endAfter: GROUP_RECURRING_DEFAULTS.endAfter,
  customFrequency: GROUP_RECURRING_DEFAULTS.customFrequency,
  customFrequencyUnit: GROUP_RECURRING_DEFAULTS.customFrequencyUnit,
  selectedWeekdays: [],

  sendForms: false,
  customForms: [],
  assessments: [],
};

const isWholeNumberAboveZero = (value?: number) =>
  value === undefined ||
  value === null ||
  (Number.isInteger(value) && value > 0);

/** Recurring rules shared by the group record and the schedule-session form. */
export const RECURRING_SCHEMA_FIELDS = {
  isRecurring: Yup.boolean(),
  recurringFrequency: Yup.string().when("isRecurring", {
    is: true,
    then: (schema) =>
      schema.required(
        GROUP_FIELDS_VALIDATION_MESSAGE.recurringFrequencyRequired
      ),
  }),
  endType: Yup.string().when("isRecurring", {
    is: true,
    then: (schema) =>
      schema.required(GROUP_FIELDS_VALIDATION_MESSAGE.endTypeRequired),
  }),
  endDate: Yup.mixed()
    .nullable()
    .when(["isRecurring", "endType"], {
      is: (isRecurring: boolean, endType: EndType) =>
        isRecurring && endType === RECURRING_END_TYPE.ON_DATE,
      then: (schema) =>
        schema
          .required(GROUP_FIELDS_VALIDATION_MESSAGE.endDateRequired)
          .test(
            "after-start",
            GROUP_VALIDATION_MESSAGE.endDateBeforeStart,
            function (value) {
              const startDate = this.parent.startDate;
              if (!value || !startDate) return true;
              return !(
                value as { isBefore: (d: unknown, unit: string) => boolean }
              ).isBefore(startDate, "day");
            }
          ),
    }),
  endAfter: Yup.number().when(["isRecurring", "endType"], {
    is: (isRecurring: boolean, endType: EndType) =>
      isRecurring && endType === RECURRING_END_TYPE.AFTER_N_EVENTS,
    then: (schema) =>
      schema
        .typeError(GROUP_FIELDS_VALIDATION_MESSAGE.endAfterRequired)
        .required(GROUP_FIELDS_VALIDATION_MESSAGE.endAfterRequired)
        .test(
          "whole-number",
          GROUP_FIELDS_VALIDATION_MESSAGE.endAfterInvalid,
          isWholeNumberAboveZero
        ),
  }),
  customFrequency: Yup.number().when(["isRecurring", "recurringFrequency"], {
    is: (isRecurring: boolean, recurringFrequency: RecurringFrequency) =>
      isRecurring && recurringFrequency === RECURRING_FREQUENCY.CUSTOM,
    then: (schema) =>
      schema
        .typeError(GROUP_FIELDS_VALIDATION_MESSAGE.customFrequencyRequired)
        .required(GROUP_FIELDS_VALIDATION_MESSAGE.customFrequencyRequired)
        .test(
          "whole-number",
          GROUP_FIELDS_VALIDATION_MESSAGE.customFrequencyInvalid,
          isWholeNumberAboveZero
        ),
  }),
  customFrequencyUnit: Yup.string().when(
    ["isRecurring", "recurringFrequency"],
    {
      is: (isRecurring: boolean, recurringFrequency: RecurringFrequency) =>
        isRecurring && recurringFrequency === RECURRING_FREQUENCY.CUSTOM,
      then: (schema) =>
        schema.required(
          GROUP_FIELDS_VALIDATION_MESSAGE.customFrequencyUnitRequired
        ),
    }
  ),
  selectedWeekdays: Yup.array().when(
    ["isRecurring", "recurringFrequency", "customFrequencyUnit"],
    {
      is: (
        isRecurring: boolean,
        recurringFrequency: RecurringFrequency,
        customFrequencyUnit: CustomFrequencyUnit
      ) =>
        isRecurring &&
        recurringFrequency === RECURRING_FREQUENCY.CUSTOM &&
        customFrequencyUnit === CUSTOM_FREQUENCY_UNIT.WEEKS,
      then: (schema) =>
        schema.min(1, GROUP_FIELDS_VALIDATION_MESSAGE.selectedWeekdaysRequired),
    }
  ),
};

export const GROUP_FIELDS_SCHEMA = Yup.object({
  groupId: Yup.string()
    .nullable()
    .required(GROUP_FIELDS_VALIDATION_MESSAGE.groupNameRequired),
  startDate: Yup.mixed()
    .nullable()
    .required(GROUP_FIELDS_VALIDATION_MESSAGE.startDateRequired),
  startTime: Yup.string()
    .nullable()
    .required(GROUP_FIELDS_VALIDATION_MESSAGE.slotRequired),
  locationId: Yup.object()
    .nullable()
    .when("mode", {
      is: VISIT_MODE.IN_PERSON,
      then: (schema) =>
        schema.required(GROUP_FIELDS_VALIDATION_MESSAGE.locationRequired),
    }),
  ...RECURRING_SCHEMA_FIELDS,
});

export const PROVIDER_ASSIGNMENT_LABEL = {
  title: "Provider Assignment",
  primaryTherapist: "Primary therapist",
  coTherapist: "Co-therapist / facilitator",
  optional: "(optional)",
  placeholder: "Select a provider...",
  save: "Save Details",
} as const;

export const PROVIDER_ASSIGNMENT_MESSAGE = {
  primaryRequired: "Primary therapist is required",
  sameAsPrimary: "Co-therapist must be a different provider",
} as const;

// ─── Check-In ────────────────────────────────────────────────────────────────

export const CHECK_IN_LABEL = {
  title: "Check-In",
  titleSeparator: " — ",
  session: "Session",
  startSession: "Start Session",
  summaryGroup: "Group",
  summaryWhen: "When",
  summaryTherapist: "Therapist",
  emptyRoster: "No patients are enrolled in this session yet.",
  refresh: "Refresh",
} as const;

export const CHECK_IN_COLUMN_LABEL = {
  patient: "Patient",
  amount: "Amount",
  paymentStatus: "Payment Status",
} as const;

export const CHECK_IN_PAYMENT_STATUS = {
  PAID: "PAID",
  DUE: "DUE",
} as const;

export const CHECK_IN_PAYMENT_STATUS_LABEL: Record<string, string> = {
  [CHECK_IN_PAYMENT_STATUS.PAID]: "Paid",
  [CHECK_IN_PAYMENT_STATUS.DUE]: "Due",
};

export const CHECK_IN_ROW_ACTION = {
  COLLECT_PAYMENT: "collect-payment",
  SEND_PAYMENT_LINK: "send-payment-link",
} as const;

export const CHECK_IN_ROW_ACTION_LABEL = {
  [CHECK_IN_ROW_ACTION.COLLECT_PAYMENT]: "Collect Payment",
  [CHECK_IN_ROW_ACTION.SEND_PAYMENT_LINK]: "Send Payment Link",
} as const;

/** A group session charge is the patient's own, never an insurance copay. */
export const CHECK_IN_PAYMENT_CATEGORY = "SELF_PAY";

/** `send-payment-link` charges in this currency. */
export const PAYMENT_LINK_CURRENCY = "usd";

/** Session statuses that can no longer be started. */
export const CHECK_IN_CLOSED_SESSION_STATUSES: string[] = [
  GROUP_APPOINTMENT_STATUS.COMPLETED,
  GROUP_APPOINTMENT_STATUS.CANCELLED,
];

export const CHECK_IN_SESSION_TIME_FORMAT = GROUP_VISIT_DATE_TIME_FORMAT;
export const CHECK_IN_AMOUNT_CURRENCY = "$";

/** Roster grid height; the screen scrolls, the grid does not. */
export const CHECK_IN_TABLE_ROW_HEIGHT = 46;

/** Sessions are not numbered by an API yet. */
export const CHECK_IN_DEFAULT_SESSION_NUMBER = 1;

export const CHECK_IN_MESSAGE = {
  paymentLinkSent: "Payment link sent to",
  noEmail: "Payment link cannot be sent: no email is registered for",
  noAmount: "Payment link cannot be sent: no amount is set for",
  noPaymentAmount: "Payment cannot be collected: no amount is set for",
} as const;

// ─── Session Documentation ───────────────────────────────────────────────────

export const SESSION_NOTE_LABEL = {
  title: "Group Therapy Session Documentation",
  pendingTag: "Documentation pending",
  completeTag: "Documentation complete",
  groupNoteCaption: "Group Note",
  cancelSession: "Cancel session",
  finalizeSession: "Finalize session",
  print: "Print",
  sharedNoteTab: "Shared Group Note",
  saving: "Saving…",
  attendance: "Attendance",
  markAllPresent: "Mark all present",
  unmarkAll: "Unmark all",
  searchPlaceholder: "Search participants...",
  patientId: "Patient ID",
  completionTracker: "Note completion tracker",
  completionGroupSuffix: "+ group",
  sharedNoteTitle: "Shared Group Note",
  markComplete: "Save",
  sessionInformation: "Session Information",
  clinicalDocumentation: "Clinical Documentation",
  addTherapeuticInterventions: "Add Therapeutic Interventions",
  therapeuticInterventions: "Therapeutic Interventions",
  addNote: "Add Note",
  save: "Save",
  cancel: "Cancel",
  noNoteRequired: "No note required",
  showSummary: "Show summary",
} as const;

export const SESSION_NOTE_STATE = {
  DRAFT: "DRAFT",
  COMPLETE: "COMPLETE",
} as const;

export const SESSION_NOTE_STATE_LABEL: Record<SessionNoteState, string> = {
  [SESSION_NOTE_STATE.DRAFT]: "In Progress",
  [SESSION_NOTE_STATE.COMPLETE]: "Complete",
};

export const ATTENDANCE_STATUS = {
  PRESENT: "PRESENT",
  NO_SHOW: "NO_SHOW",
} as const;

export const ATTENDANCE_STATUS_LABEL: Record<string, string> = {
  [ATTENDANCE_STATUS.PRESENT]: "Present",
  [ATTENDANCE_STATUS.NO_SHOW]: "No show",
};

export const ATTENDANCE_STATUS_PLACEHOLDER = "Select";

export const ATTENDANCE_STATUS_OPTIONS: Option[] = [
  {
    value: ATTENDANCE_STATUS.PRESENT,
    label: ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.PRESENT],
  },
  {
    value: ATTENDANCE_STATUS.NO_SHOW,
    label: ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.NO_SHOW],
  },
];

/** Statuses that still owe an individual note. */
export const NOTE_REQUIRED_STATUSES: string[] = [ATTENDANCE_STATUS.PRESENT];

export const PARTICIPANT_NOTE_STATE = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETE: "COMPLETE",
} as const;

export const PARTICIPANT_NOTE_STATE_LABEL: Record<string, string> = {
  [PARTICIPANT_NOTE_STATE.NOT_STARTED]: "Not started",
  [PARTICIPANT_NOTE_STATE.IN_PROGRESS]: "In progress",
  [PARTICIPANT_NOTE_STATE.COMPLETE]: "Completed",
};

/** Attendance tiles, in the order the design shows them. */
export const ATTENDANCE_TILE = {
  TOTAL: "TOTAL",
  PRESENT: ATTENDANCE_STATUS.PRESENT,
  NO_SHOW: ATTENDANCE_STATUS.NO_SHOW,
} as const;

export const ATTENDANCE_TILE_LABEL: Record<string, string> = {
  [ATTENDANCE_TILE.TOTAL]: "Total",
  [ATTENDANCE_TILE.PRESENT]: "Present",
  [ATTENDANCE_TILE.NO_SHOW]: "No-show",
};

/** Tile tint per attendance state, keyed by tile id. */
export const ATTENDANCE_TILE_COLORS: Record<
  string,
  { color?: string; bgColor?: string }
> = {
  [ATTENDANCE_TILE.TOTAL]: { bgColor: CHART_COLORS.badgeDefaultBg },
  [ATTENDANCE_TILE.PRESENT]: {
    color: CHART_COLORS.badgeSuccessText,
    bgColor: CHART_COLORS.badgeSuccessBg,
  },
  [ATTENDANCE_TILE.NO_SHOW]: {
    color: CHART_COLORS.badgeErrorText,
    bgColor: CHART_COLORS.badgeErrorBg,
  },
};

export const SESSION_NOTE_FIELD_LABEL = {
  sessionObjectives: "Session objectives",
  topicsCovered: "Topics covered",
  therapeuticInterventions: "Therapeutic interventions used",
  groupActivities: "Group activities",
  homeworkAssigned: "Homework assigned",
  homework: "Homework details",
  overallGroupSummary: "Overall group summary",
} as const;

export const SESSION_NOTE_PLACEHOLDER = {
  sessionObjectives: "What the session set out to achieve...",
  topicsCovered: "Topics the group covered...",
  homework: "Homework assigned to the group...",
  overallGroupSummary: "How the group responded overall...",
  groupActivities: "Select activities",
} as const;

/** Session Information rows, read from the session and shown read-only. */
export const SESSION_INFO_FIELD = {
  GROUP_NAME: "groupName",
  SESSION_INFO: "sessionInfo",
  GROUP_TYPE: "groupType",
  PROVIDERS: "providers",
  DATE_TIME: "dateTime",
} as const;

export const SESSION_INFO_FIELD_LABEL: Record<string, string> = {
  [SESSION_INFO_FIELD.GROUP_NAME]: "Group name",
  [SESSION_INFO_FIELD.SESSION_INFO]: "Session info",
  [SESSION_INFO_FIELD.GROUP_TYPE]: "Group type",
  [SESSION_INFO_FIELD.PROVIDERS]: "Provider & co-provider",
  [SESSION_INFO_FIELD.DATE_TIME]: "Date & time",
};

/** The order the read-only session information is laid out in. */
export const SESSION_INFO_FIELDS: string[] = [
  SESSION_INFO_FIELD.GROUP_NAME,
  SESSION_INFO_FIELD.SESSION_INFO,
  SESSION_INFO_FIELD.GROUP_TYPE,
  SESSION_INFO_FIELD.PROVIDERS,
  SESSION_INFO_FIELD.DATE_TIME,
];

/** Shown where the session carries no value for a read-only field. */
export const SESSION_INFO_EMPTY_VALUE = "-";

/** The shared group note print. */
export const GROUP_SESSION_PRINT_LABEL = {
  title: "Group Therapy Session Note",
  drawerTitle: "Group Session Note",
  sessionDetails: "Group Session Details",
  participants: "Patients Participated",
  clinicalDocumentation: "Clinical Documentation",
  groupName: "Group Name",
  sessionInfo: "Session Info",
  groupType: "Group Type",
  therapyCategory: "Therapy Category",
  dateTime: "Date & Time",
  mode: "Mode",
  room: "Room",
  status: "Session Status",
  therapists: "Provider & Co-provider",
  attendees: "Enrolled Patients",
  patientName: "Patient Name",
  mrn: "MRN",
  birthDate: "Date of Birth",
  email: "Email",
  serialNumber: "#",
  noParticipants: "No patients are enrolled in this session.",
  signedBy: "Session Signed by",
} as const;

/** Mirrors the individual visit note's signature line. */
export const GROUP_SESSION_SIGNED_LINE = {
  prefix: "Signed by",
  on: "on",
  at: "at",
} as const;

export const GROUP_SESSION_PRINT_FILE_NAME = "group-session-note.pdf";
export const SESSION_NOTE_PRINT_DRAWER_WIDTH = "65vw";
export const GROUP_SESSION_PRINT_DATE_FORMAT = DATE_FORMAT;
export const YES_NO_LABEL = { yes: "Yes", no: "No" } as const;

export const THERAPEUTIC_INTERVENTIONS_KEY = "therapeuticInterventions";
export const THERAPEUTIC_INTERVENTIONS_NOTE_KEY =
  "therapeuticInterventionsNote";

export const THERAPEUTIC_INTERVENTION_SECTIONS: SessionCheckboxSection[] = [
  {
    key: THERAPEUTIC_INTERVENTIONS_KEY,
    label: "Therapeutic Interventions",
    noteKey: THERAPEUTIC_INTERVENTIONS_NOTE_KEY,
    items: [
      { name: "activeListening", label: "Active Listening" },
      { name: "supportiveTherapy", label: "Supportive Therapy" },
      {
        name: "cognitiveBehavioralTherapyCbt",
        label: "Cognitive Behavioral Therapy (CBT)",
      },
      {
        name: "dialecticalBehaviorTherapyDbt",
        label: "Dialectical Behavior Therapy (DBT)",
      },
      {
        name: "acceptanceAndCommitmentTherapyAct",
        label: "Acceptance and Commitment Therapy (ACT)",
      },
      { name: "motivationalInterviewing", label: "Motivational Interviewing" },
      { name: "solutionFocusedTherapy", label: "Solution-Focused Therapy" },
      { name: "traumaInformedCare", label: "Trauma-Informed Care" },
      { name: "psychoeducation", label: "Psychoeducation" },
      { name: "mindfulnessTraining", label: "Mindfulness Training" },
      { name: "relaxationTechniques", label: "Relaxation Techniques" },
      { name: "breathingExercises", label: "Breathing Exercises" },
      {
        name: "emotionalRegulationSkills",
        label: "Emotional Regulation Skills",
      },
      { name: "distressToleranceSkills", label: "Distress Tolerance Skills" },
      { name: "cognitiveRestructuring", label: "Cognitive Restructuring" },
      { name: "behavioralActivation", label: "Behavioral Activation" },
      { name: "exposureTechniques", label: "Exposure Techniques" },
      { name: "safetyPlanning", label: "Safety Planning" },
      { name: "crisisIntervention", label: "Crisis Intervention" },
      { name: "medicationEducation", label: "Medication Education" },
      { name: "familyEducation", label: "Family Education" },
      { name: "goalSetting", label: "Goal Setting" },
      { name: "problemSolving", label: "Problem-Solving" },
      {
        name: "communicationSkillsTraining",
        label: "Communication Skills Training",
      },
      { name: "copingSkillsTraining", label: "Coping Skills Training" },
      { name: "relapsePrevention", label: "Relapse Prevention" },
    ],
  },
];

/** Selected group activities shown as chips before the "+N" overflow. */
export const GROUP_ACTIVITY_VISIBLE_TAGS = 4;

export const GROUP_ACTIVITY_OPTIONS: GroupActivityOption[] = [
  { value: "cbtThoughtChallenging", label: "CBT Thought-Challenging" },
  {
    value: "cognitiveDistortionsIdentification",
    label: "Cognitive Distortions Identification",
  },
  { value: "goalSetting", label: "Goal Setting" },
  { value: "problemSolvingExercises", label: "Problem-Solving Exercises" },
  { value: "stressManagement", label: "Stress Management" },
  { value: "anxietyManagement", label: "Anxiety Management" },
  { value: "mindfulnessAndMeditation", label: "Mindfulness & Meditation" },
  { value: "groundingTechniques", label: "Grounding Techniques" },
  { value: "breathingExercises", label: "Breathing Exercises" },
  { value: "emotionRegulation", label: "Emotion Regulation" },
  { value: "angerManagement", label: "Anger Management" },
  { value: "dbtSkillsPractice", label: "DBT Skills Practice" },
  { value: "communicationSkills", label: "Communication Skills" },
  { value: "assertivenessTraining", label: "Assertiveness Training" },
  { value: "boundarySetting", label: "Boundary Setting" },
  { value: "rolePlaying", label: "Role-Playing" },
  { value: "copingSkillsDevelopment", label: "Coping Skills Development" },
  { value: "triggerIdentification", label: "Trigger Identification" },
  { value: "relapsePrevention", label: "Relapse Prevention" },
  { value: "psychoeducation", label: "Psychoeducation" },
  { value: "selfEsteemBuilding", label: "Self-Esteem Building" },
  { value: "selfCompassionExercises", label: "Self-Compassion Exercises" },
  { value: "griefAndLossProcessing", label: "Grief & Loss Processing" },
  { value: "traumaCopingSkills", label: "Trauma Coping Skills" },
  {
    value: "peerSupportAndExperienceSharing",
    label: "Peer Support & Experience Sharing",
  },
  { value: "recoveryGoalPlanning", label: "Recovery Goal Planning" },
  { value: "journaling", label: "Journaling" },
  { value: "artCreativeExpression", label: "Art/Creative Expression" },
  { value: "guidedVisualization", label: "Guided Visualization" },
  {
    value: "weeklyCheckInAndGoalReview",
    label: "Weekly Check-In & Goal Review",
  },
];

export const SESSION_NOTE_INITIAL_VALUES: SessionNoteValues = {
  sessionObjectives: "",
  topicsCovered: "",
  therapeuticInterventions: "",
  therapeuticInterventionsNote: "",
  groupActivities: [],
  isHomeworkAssigned: false,
  homework: "",
  overallGroupSummary: "",
};

/** Height of the shared group note text areas. */
export const SESSION_NOTE_TEXTAREA_ROWS = 3;

/** Checkbox cards lay their options out in three columns. */
export const CHECKBOX_COLUMN_SIZE = 4;
export const CHECKBOX_NOTE_ROWS = 2;

export const CHECKBOX_MODAL_SX = {
  "& .MuiDialog-paper": { width: "60vw", height: "80vh" },
};

export const SESSION_NOTE_MESSAGE = {
  sessionCancelled: "Session cancelled",
  sessionFinalized: "Session finalized",
  allMarkedPresent: "All participants marked present",
  allUnmarked: "Attendance cleared for all participants",
  sessionNotFound: "This session could not be found.",
  sessionLocked: "This session is closed and can no longer be documented.",
  finalizeBlocked:
    "Record attendance and complete the shared group note before completing the session.",
} as const;

/** Sent as the sign-off note when the shared note carries no summary. */
export const SESSION_SIGN_OFF_NOTE = "Group session reviewed and signed.";

export const SESSION_NOTE_CANCEL_CONFIRMATION = {
  title: "Cancel this session?",
  content:
    "Any information entered for this session will be lost. The session will be marked Canceled and will not be sent to billing.",
  confirm: "Cancel session",
  keep: "Keep session",
} as const;

/** Session statuses under which documentation is read-only. */
export const SESSION_DOCUMENTATION_LOCKED_STATUSES: string[] = [
  GROUP_APPOINTMENT_STATUS.COMPLETED,
  GROUP_APPOINTMENT_STATUS.CANCELLED,
];

/** Everyone starts as present: the roster was already checked in. */
export const SESSION_NOTE_TIME_FORMAT = GROUP_VISIT_TIME_FORMAT;
export const SESSION_NOTE_TIME_RANGE_SEPARATOR = "-";
export const SESSION_NOTE_CO_THERAPIST_SUFFIX = "(co)";

/** Width of the attendance rail beside the note. */
export const ATTENDANCE_PANEL_WIDTH = 310;
export const SESSION_NOTE_HEADER_SEPARATOR = " · ";
export const SESSION_NOTE_DATE_FORMAT = DATE_FORMAT;

export const FINALIZE_SESSION_LABEL = {
  title: "Finalize session documentation?",
  attendanceRecorded: "Attendance recorded",
  sharedGroupNote: "Shared group note",
  individualNotes: "Individual notes",
  complete: "Complete",
  draft: "Draft",
  incompleteWarning:
    "Some individual notes are not signed off yet. Complete them (no-shows excepted) before finalizing the session.",
  keepEditing: "Keep editing",
  finalizeAll: "Finalize session",
} as const;

export const FINALIZE_CHECK_STATE = {
  COMPLETE: "COMPLETE",
  PARTIAL: "PARTIAL",
} as const;

// ─── Individual progress note ────────────────────────────────────────────────

export const INDIVIDUAL_NOTE_LABEL = {
  backToSession: "Back to Session",
  privacyPrefix: "Privacy:",
  prev: "Prev",
  nextAttendee: "Next attendee",
  markComplete: "Mark note complete",
} as const;

export const INDIVIDUAL_NOTE_MESSAGE = {
  missingProvider: "Sign in as a provider to complete this note.",
} as const;

export const INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION = {
  title: "Leave this note unsigned?",
  content:
    "This patient's note is not marked complete, so changes made to it will not be kept. You can come back to complete it before completing the session.",
  confirm: "Continue",
  cancel: "Stay here",
} as const;

/**
 * The note forms read the patient and appointment they belong to from the URL,
 * so the selected participant is mirrored there.
 */
export const ENCOUNTER_APPOINTMENT_QUERY_PARAM = "appointmentid";
export const ENCOUNTER_PATIENT_QUERY_PARAM = "patientId";

/** Width of the visit note template picker. */
export const NOTE_TEMPLATE_SELECT_WIDTH = 220;
